/*
 * minesweeper/ui.js — the page of /minesweeper.
 *
 * The rules live in engine.js, the logic in solver.js, the boards in
 * generator.js (and worker.js runs both off the main thread), the gestures in
 * gesture.js. This file is the page: it draws the board the server already
 * rendered, sizes it to the screen, turns gestures into moves, and says what
 * happened — the hint with its proof, the verdict after a loss.
 *
 * Decisions and measurements — docs/why/minesweeper.md.
 */
(function () {
    'use strict';

    var Core = window.MinesCore, Solver = window.MinesSolver, Gen = window.MinesGen, Kit = window.GameKit, Gesture = window.MinesGesture;
    var CFG = window.__MS_CFG || {}, T = CFG.t || {};
    var SLUG = 'minesweeper';
    // The inline check in the blade reads the same three keys (as ts_minesweeper_*).
    var KEY_GAME = 'game', KEY_LEVEL = 'level', KEY_PREFS = 'prefs';

    function $(id) { return document.getElementById(id); }
    var app = $('msApp'), stage = $('msStage'), view = $('msView'), board = $('msBoard');
    if (!app || !board) return;
    if (!Core || !Solver || !Gen || !Kit || !Gesture) {
        // The first screen is in the markup; without the engine it must not look alive.
        try { if (window.__earlyAction) window.__earlyAction.claim(SLUG); } catch (_) {}
        try { if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'minesweeper_engine_missing' }); } catch (_) {}
        return;
    }
    var OPEN = Core.OPEN, FLAG = Core.FLAG, QUESTION = Core.QUESTION, HIDDEN = Core.HIDDEN;

    function tr(key, vars) {
        var s = T[key] == null ? key : String(T[key]);
        if (vars) {
            Object.keys(vars).sort(function (a, b) { return b.length - a.length; }).forEach(function (k) {
                s = s.replace(new RegExp(':' + k + '(?![a-z_])', 'g'), String(vars[k]));
            });
        }
        return s;
    }
    function TS() { return window.__toolSettings; }
    function load(key, fallback) { try { var s = TS(); return s ? s.load(SLUG, key, fallback) : fallback; } catch (_) { return fallback; } }
    function save(key, value) { try { var s = TS(); if (s) s.save(SLUG, key, value); } catch (_) {} }
    function drop(key) { try { localStorage.removeItem('ts_' + SLUG + '_' + key); } catch (_) {} }
    function ae(f, data) {
        try { if (typeof window.__ae === 'function') window.__ae('feature_use', Object.assign({ slug: SLUG, f: f }, data || {})); } catch (_) {}
    }
    function toast(msg, type) { try { if (typeof window.__toast === 'function') window.__toast(msg, type || 'info'); } catch (_) {} }
    function say(msg) { try { if (typeof window.__announce === 'function') window.__announce(msg); } catch (_) {} }
    function setText(el, text) {
        if (window.__setText) return window.__setText(el, text);
        if (el) el.textContent = text;
    }
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }
    function seed32() {
        try { var a = new Uint32Array(1); window.crypto.getRandomValues(a); return a[0] >>> 0; }
        catch (_) { return (Date.now() ^ Math.floor(performance.now() * 1000)) >>> 0; }
    }

    // ── Preferences ──────────────────────────────────────────────────────────
    var HOLD = { fast: 250, normal: 350, slow: 500 };
    var DEFAULTS = {
        level: 'beginner', cw: 16, ch: 16, cm: 40, ng: true, questions: false, chord: true, hold: 'normal',
        vibrate: true, sound: false, skin: 'modern', palette: false, left: false, rotate: true, cell: 0, flagMode: false
    };
    var prefs = (function () {
        var saved = load(KEY_PREFS, null), p = {};
        for (var k in DEFAULTS) p[k] = (saved && typeof saved[k] === typeof DEFAULTS[k]) ? saved[k] : DEFAULTS[k];
        if (!Core.LEVELS[p.level] && p.level !== 'custom') p.level = 'beginner';
        if (!HOLD[p.hold]) p.hold = 'normal';
        return p;
    })();
    function savePrefs() { save(KEY_PREFS, prefs); save(KEY_LEVEL, prefs.level); }

    var coarse = !!(window.matchMedia && window.matchMedia('(pointer:coarse)').matches);
    var lastInput = coarse ? 'touch' : 'mouse';

    // ── State ────────────────────────────────────────────────────────────────
    var game = null, meta = null;
    var cells = [], colsD = 0, rowsD = 0, turned = false, curCell = 0, autoCell = 0;
    var marks = {}, oddsOn = false, oddsShown = false, focusIdx = 0;
    var busy = false, pre = null, bootMoved = false;
    var stats = Kit.stats(SLUG);
    var clock = Kit.clock({ tickMs: 200, onTick: function (ms) { setText($('msTime'), Kit.formatTime(ms)); } });
    var ring = $('msRing');

    function dimsOf(level) {
        if (Core.LEVELS[level]) return Core.LEVELS[level];
        return Core.clampBoard(prefs.cw, prefs.ch, prefs.cm);
    }
    function variantId() { return meta.level + ':' + (meta.ng ? 'ng' : 'cl'); }
    function levelName(level) { return tr('level_' + level); }

    // ── Storage that survives a crash ────────────────────────────────────────
    // localStorage is the fast copy (read before the first paint of the next
    // load); it reaches the disk about once a minute, so a killed browser loses
    // it. The durable copy goes through the site's work storage (IndexedDB,
    // strict durability). On load the newer of the two wins.
    var durable = (function () {
        var dbP = null;
        function open() {
            if (dbP) return dbP;
            var ws = window.__workStorage;
            if (!ws || typeof ws.open !== 'function') return (dbP = Promise.resolve(null));
            dbP = ws.open('timbrica_minesweeper', 1, function (db) {
                if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
            }, { stamp: function (v) { return (v && v.at) || 0; } }).then(function (r) { return r && r.db; }, function () { return null; });
            return dbP;
        }
        function run(mode, fn) {
            return open().then(function (db) {
                if (!db) return null;
                return new Promise(function (resolve) {
                    var out = null, t;
                    try {
                        t = mode === 'w' ? window.__workStorage.tx(db, 'kv') : db.transaction('kv', 'readonly');
                        var rq = fn(t.objectStore('kv'));
                        if (rq) rq.onsuccess = function () { out = rq.result; };
                        t.oncomplete = function () { resolve(out); };
                        t.onerror = t.onabort = function () { resolve(null); };
                    } catch (_) { resolve(null); }
                });
            }).catch(function () { return null; });
        }
        return {
            put: function (value) { return run('w', function (st) { st.put(value, 'game'); }); },
            get: function () { return run('r', function (st) { return st.get('game'); }); },
            clear: function () { return run('w', function (st) { st.delete('game'); }); }
        };
    })();

    var saveTimer = 0, durableRead = false;
    function persist() {
        if (!game || !meta) return;
        var worth = game.status === 'playing' || (game.status === 'ready' && game.flags > 0 && !meta.fixed);
        // Until the durable copy has been looked at, an empty new board must not
        // erase it: after a crash it may be the only copy of the game there is.
        if (!worth || meta.covered) { if (durableRead) forget(); else drop(KEY_GAME); return; }
        durableRead = true;
        var snap = {
            v: 1, at: Date.now(), ms: clock.elapsed(), g: game.snapshot(),
            m: {
                level: meta.level, ng: meta.ng ? 1 : 0, first: meta.first, daily: meta.daily || null, rank: meta.rank,
                beat: meta.beat || 0, fixed: meta.fixed ? 1 : 0, ngFail: meta.ngFail ? 1 : 0,
                told: meta.told ? Kit.share.toBase64Url(Core.packBits(meta.told)) : null
            }
        };
        save(KEY_GAME, snap);
        durable.put(snap);
    }
    function forget() {
        drop(KEY_GAME);
        durable.clear();
    }
    function restore(snap) {
        if (!snap || snap.v !== 1 || !snap.m || !snap.g) return false;
        if (Date.now() - (snap.at || 0) > 30 * 86400000) return false;
        var g = Core.Game.restore(snap.g);
        if (!g || g.over()) return false;
        var level = Core.levelOf(g.w, g.h, g.count);
        if (snap.m.level !== level && !(snap.m.level === 'custom')) return false;
        game = g;
        meta = blankMeta(snap.m.level, !!snap.m.ng);
        meta.first = typeof snap.m.first === 'number' ? snap.m.first : -1;
        meta.daily = snap.m.daily || null;
        meta.rank = snap.m.rank || 'none';
        meta.beat = snap.m.beat || 0;
        meta.fixed = !!snap.m.fixed;
        meta.ngFail = !!snap.m.ngFail;
        if (snap.m.told) {
            var bytes = Kit.share.fromBase64Url(snap.m.told);
            if (bytes) meta.told = Core.unpackBits(bytes, g.n);
        }
        if (snap.m.level === 'custom') { prefs.cw = g.w; prefs.ch = g.h; prefs.cm = g.count; }
        prefs.level = snap.m.level;
        clock.reset(snap.ms || 0);
        clearMarks();
        showResult(null);
        setCover(null);
        layout(true);
        syncChrome();
        if (game.status === 'playing') clock.start();
        return true;
    }

    // ── The worker: boards and analysis ──────────────────────────────────────
    var worker = null, workerDead = false, wseq = 0, waiting = {};
    function ensureWorker() {
        if (worker || workerDead || !CFG.worker || typeof Worker !== 'function') return worker;
        try {
            worker = new Worker(CFG.worker);
            worker.onmessage = function (e) {
                var m = e.data || {};
                if (m.type === 'pool') return;
                var w = waiting[m.id];
                if (!w) return;
                delete waiting[m.id];
                clearTimeout(w.timer);
                if (m.type === 'error') w.reject(new Error(m.message || 'worker'));
                else w.resolve(m);
            };
            worker.onerror = function (e) {
                // Handled: the boards fall back to the main thread (localBoard) and
                // worker_failed counts it. Without this the same failure also reached
                // the page's error log as an uncaught one (TIMBRICA-FRONTEND-RS, 27.10:
                // the worker ran from a blob: URL on the visitor's side, so its
                // relative importScripts had no base).
                if (e && e.preventDefault) e.preventDefault();
                workerDead = true;
                worker = null;
                for (var id in waiting) { clearTimeout(waiting[id].timer); waiting[id].reject(new Error('worker-failed')); }
                waiting = {};
                ae('worker_failed', {});
            };
        } catch (_) { workerDead = true; worker = null; }
        return worker;
    }
    function ask(msg, timeoutMs) {
        return new Promise(function (resolve, reject) {
            var w = ensureWorker();
            if (!w) { reject(new Error('no-worker')); return; }
            msg.id = ++wseq;
            waiting[msg.id] = {
                resolve: resolve, reject: reject,
                timer: setTimeout(function () { delete waiting[msg.id]; reject(new Error('worker-timeout')); }, timeoutMs || 4000)
            };
            w.postMessage(msg);
        });
    }
    function poolFor() {
        var w = ensureWorker();
        if (!w || !game) return;
        if (meta.ng && !meta.fixed && game.status === 'ready') w.postMessage({ type: 'pool', w: game.w, h: game.h, mines: game.count });
    }
    // The same answers on the main thread — when the worker cannot be had.
    function localBoard(m) {
        var mine, ok = true;
        if (m.mode !== 'ng') mine = Gen.classic(m.w, m.h, m.mines, m.first, m.seed != null ? m.seed : seed32());
        else {
            var t0 = performance.now();
            var g = Gen.noGuess(m.w, m.h, m.mines, m.first, m.seed != null ? m.seed : seed32(),
                m.seed != null ? null : { expired: function () { return performance.now() - t0 > 2500; } });
            mine = g.mine; ok = g.ok;
        }
        return { mine: mine, ok: ok, from: 'local' };
    }
    function requestBoard(first, seed) {
        var standard = meta.level !== 'custom';
        var m = { type: 'gen', w: game.w, h: game.h, mines: game.count, first: first, mode: meta.ng ? 'ng' : 'classic' };
        if (seed != null) m.seed = seed;
        else if (!standard && meta.ng) m.deadlineMs = 6000;
        return ask(m, standard ? 4000 : 10000).then(null, function () { return localBoard(m); });
    }
    function analyse(type, payload, fallback) {
        payload.type = type;
        return ask(payload, 5000).then(function (m) { return m.result; }, function () { return fallback(); });
    }

    // ── The board in the page ────────────────────────────────────────────────
    function cellIndex(dc, dr) { return turned ? dc * game.w + dr : dr * game.w + dc; }
    function cellPos(i) {
        var x = i % game.w, y = (i - x) / game.w;
        return turned ? { c: y, r: x } : { c: x, r: y };
    }
    function buildBoard() {
        colsD = turned ? game.h : game.w;
        rowsD = turned ? game.w : game.h;
        var existing = board.querySelectorAll('.ms-c'), i;
        cells = new Array(game.n);
        if (!turned && existing.length === game.n && board.getAttribute('data-dims') === game.w + 'x' + game.h) {
            for (i = 0; i < existing.length; i++) cells[+existing[i].getAttribute('data-i')] = existing[i];
        } else if (!turned && existing.length === game.n && !board.hasAttribute('data-dims') && game.w === 9 && game.h === 9) {
            // The board the server rendered: take it as it is.
            for (i = 0; i < existing.length; i++) {
                existing[i].removeAttribute('data-early-action');
                cells[+existing[i].getAttribute('data-i')] = existing[i];
            }
        } else {
            var frag = document.createDocumentFragment();
            for (var dr = 0; dr < rowsD; dr++) {
                var row = document.createElement('div');
                row.className = 'ms-row';
                row.setAttribute('role', 'row');
                for (var dc = 0; dc < colsD; dc++) {
                    var idx = cellIndex(dc, dr), el = document.createElement('div');
                    el.className = 'ms-c';
                    el.setAttribute('role', 'gridcell');
                    el.setAttribute('tabindex', '-1');
                    el.setAttribute('data-i', idx);
                    cells[idx] = el;
                    row.appendChild(el);
                }
                frag.appendChild(row);
            }
            var old = board.querySelectorAll('.ms-row');
            for (i = 0; i < old.length; i++) board.removeChild(old[i]);
            board.insertBefore(frag, ring);
        }
        board.setAttribute('data-dims', game.w + 'x' + game.h);
        board.setAttribute('data-turned', turned ? '1' : '0');
        stage.style.setProperty('--ms-cols', colsD);
        for (i = 0; i < game.n; i++) { cells[i]._cls = null; cells[i]._txt = null; cells[i]._lab = null; }
        if (focusIdx >= game.n) focusIdx = 0;
        cells[focusIdx].setAttribute('tabindex', '0');
        paintAll();
    }

    function paint(i) {
        var el = cells[i], s = game.st[i], cls = 'ms-c', txt = '', n = '', lab, lost = game.status === 'lost';
        if (s === OPEN) {
            if (game.mine && game.mine[i]) { cls += ' is-mine is-boom'; lab = tr('cell_mine'); }
            else {
                cls += ' is-open';
                var d = game.num[i];
                if (d) { txt = String(d); n = txt; lab = txt; } else lab = tr('cell_empty');
            }
        } else if (s === FLAG) {
            cls += ' is-flag';
            if (lost && !game.mine[i]) cls += ' is-wrong';
            lab = tr('cell_flag');
        } else if (s === QUESTION) {
            cls += ' is-q'; txt = '?'; lab = tr('cell_question');
        } else if (lost && game.mine && game.mine[i]) {
            cls += ' is-mine'; lab = tr('cell_mine');
        } else lab = tr('cell_hidden');
        var mark = marks[i];
        if (mark) {
            if (mark.cls) cls += ' ' + mark.cls;
        }
        if (el._cls !== cls) { el.className = cls; el._cls = cls; }
        if (el._txt !== txt) { el.textContent = txt; el._txt = txt; }
        if (n) el.setAttribute('data-n', n); else if (el.hasAttribute('data-n')) el.removeAttribute('data-n');
        var p = mark && mark.p != null ? mark.p : null;
        if (p != null) { el.setAttribute('data-p', Math.round(p * 100)); el.style.setProperty('--ms-p', p.toFixed(3)); }
        else if (el.hasAttribute('data-p')) { el.removeAttribute('data-p'); el.style.removeProperty('--ms-p'); }
        if (el._lab !== lab) {
            var pos = cellPos(i);
            el.setAttribute('aria-label', tr('cell_at', { row: pos.r + 1, col: pos.c + 1 }) + ', ' + lab);
            el._lab = lab;
        }
    }
    function paintAll() { for (var i = 0; i < game.n; i++) paint(i); }
    function paintList(list) { for (var i = 0; i < list.length; i++) paint(list[i]); }

    function clearMarks() {
        var keys = Object.keys(marks);
        if (!keys.length) return;
        marks = {};
        oddsShown = false;
        if (cells.length) for (var i = 0; i < keys.length; i++) if (cells[+keys[i]]) paint(+keys[i]);
    }
    function setMark(i, cls, p) {
        var m = marks[i] || (marks[i] = { cls: '', p: null });
        if (cls) m.cls = m.cls ? m.cls + ' ' + cls : cls;
        if (p != null) m.p = p;
    }

    // ── Size: fit the board to the screen, turn a wide one on a tall screen ──
    function measure() {
        var full = stage.classList.contains('gk-full'), classic = app.getAttribute('data-skin') === 'classic';
        // On the page the tray is as wide as the board, so the room is the stage's own.
        var room = view.clientWidth;
        if (!full) {
            var cs = window.getComputedStyle(stage);
            room = stage.clientWidth - (parseFloat(cs.paddingLeft) || 0) - (parseFloat(cs.paddingRight) || 0);
        }
        var w = Math.max(120, room - (classic ? 6 : 16));
        var h = full ? Math.max(120, view.clientHeight - 16) : (coarse ? 1e9 : Math.max(240, window.innerHeight - 270));
        return { w: w, h: h, full: full };
    }
    function layout(rebuild) {
        if (!game) return;
        var box = measure(), gap = app.getAttribute('data-skin') === 'classic' ? 0 : 2;
        // Under a finger a cell smaller than `floor` is a mis-tap waiting to happen:
        // the board is given comfortable cells and scrolls instead. If it can at
        // least fit along one side at a size between the two, that size is taken —
        // the board then moves along one axis only.
        var floor = coarse ? (box.full ? 24 : 27) : 18, comfy = coarse ? 32 : 18, top = box.full ? (coarse ? 56 : 48) : (coarse ? 44 : 40);
        function plan(cols, rows) {
            var f = Math.floor(Math.min((box.w + gap) / cols, (box.h + gap) / rows) - gap);
            var one = Math.floor(Math.max((box.w + gap) / cols, (box.h + gap) / rows) - gap);
            var cell = f >= floor ? Math.min(f, top) : ((one >= floor && one <= comfy) ? one : comfy);
            var bw = cols * (cell + gap) - gap, bh = rows * (cell + gap) - gap;
            return { cell: cell, axes: (bw > box.w + 1 ? 1 : 0) + (bh > box.h + 1 ? 1 : 0), seen: Math.min(1, box.w / bw) * Math.min(1, box.h / bh) };
        }
        // A wide board on a tall screen is turned when the turned board is the
        // better one to play: it scrolls along fewer axes, or shows clearly more of
        // itself, or (both whole) gets clearly bigger cells. Judged on the FINAL
        // cell size — 30×16 on a phone held upright in full screen fits neither way
        // and gets 32-px cells either way, and then the unturned board only slides
        // sideways while the turned one would slide both ways.
        var pN = plan(game.w, game.h), pT = plan(game.h, game.w), wantTurn = false;
        if (prefs.rotate && game.w > game.h && box.h > box.w) {
            if (pT.axes !== pN.axes) wantTurn = pT.axes < pN.axes;
            else if (pN.seen < 1 || pT.seen < 1) wantTurn = pT.seen > pN.seen * 1.15;
            else wantTurn = pT.cell > pN.cell * 1.15;
        }
        if (rebuild || wantTurn !== turned || !cells.length) { turned = wantTurn; buildBoard(); }
        autoCell = (turned ? pT : pN).cell;
        curCell = prefs.cell ? clamp(prefs.cell, 14, 72) : autoCell;
        applyCell(curCell);
    }
    function applyCell(px) {
        stage.style.setProperty('--ms-cell', px + 'px');
        stage.style.setProperty('--ms-ring', Math.max(64, Math.round(px * 1.9)) + 'px');
    }
    function zoom(dir) {
        var keepX = view.scrollWidth > view.clientWidth ? (view.scrollLeft + view.clientWidth / 2) / view.scrollWidth : 0.5;
        var keepY = view.scrollHeight > view.clientHeight ? (view.scrollTop + view.clientHeight / 2) / view.scrollHeight : 0.5;
        var next = clamp(curCell + dir * 4, 14, 72);
        prefs.cell = Math.abs(next - autoCell) < 2 ? 0 : next;
        savePrefs();
        layout(false);
        view.scrollLeft = keepX * view.scrollWidth - view.clientWidth / 2;
        view.scrollTop = keepY * view.scrollHeight - view.clientHeight / 2;
        ae('zoom', { cell: curCell, auto: autoCell });
    }

    // Two fingers resize the board. The gesture is taken from the browser at
    // its start (touchmove is cancelled while two fingers are down), so the
    // page does not zoom and the board does not scroll under the pinch.
    // While the fingers move, the board is only SCALED (a compositor transform:
    // no layout, smooth on a slow phone); the real cell size is laid out once,
    // when the fingers lift. Measured in docs/why/minesweeper.md: re-laying 480
    // cells on every step of a pinch cost 330 ms a step at CPU ×4.
    var pinch = null;
    function touchDist(t) { return Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY); }
    function touchMid(t) { return { x: (t[0].clientX + t[1].clientX) / 2, y: (t[0].clientY + t[1].clientY) / 2 }; }
    view.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 2) { if (pinch) endPinch(); return; }
        var r = board.getBoundingClientRect(), m = touchMid(e.touches);
        pinch = { d: touchDist(e.touches) || 1, cell: curCell, bx: m.x - r.left, by: m.y - r.top, mx: m.x, my: m.y, x0: m.x, y0: m.y, s: 1, raf: 0 };
        board.style.transformOrigin = pinch.bx + 'px ' + pinch.by + 'px';
        board.style.willChange = 'transform';
        if (e.cancelable) e.preventDefault();
    }, { passive: false });
    view.addEventListener('touchmove', function (e) {
        if (!pinch || e.touches.length !== 2) return;
        if (e.cancelable) e.preventDefault();
        var m = touchMid(e.touches);
        pinch.s = clamp(pinch.cell * touchDist(e.touches) / pinch.d, 14, 72) / pinch.cell;
        pinch.mx = m.x; pinch.my = m.y;
        if (pinch.raf) return;
        pinch.raf = requestAnimationFrame(function () {
            if (!pinch) return;
            pinch.raf = 0;
            board.style.transform = 'translate(' + (pinch.mx - pinch.x0) + 'px,' + (pinch.my - pinch.y0) + 'px) scale(' + pinch.s + ')';
        });
    }, { passive: false });
    function endPinch() {
        if (!pinch) return;
        var p = pinch;
        pinch = null;
        if (p.raf) cancelAnimationFrame(p.raf);
        board.style.transform = '';
        board.style.willChange = '';
        var px = Math.round(p.cell * p.s);
        if (px === curCell) return;
        prefs.cell = Math.abs(px - autoCell) < 2 ? 0 : px;
        var before = curCell;
        curCell = prefs.cell || autoCell;
        applyCell(curCell);
        // The point that was between the fingers stays where the fingers left it.
        var gap = app.getAttribute('data-skin') === 'classic' ? 0 : 2, k = (curCell + gap) / (before + gap), r = board.getBoundingClientRect();
        view.scrollLeft += (r.left + p.bx * k) - p.mx;
        view.scrollTop += (r.top + p.by * k) - p.my;
        savePrefs();
        ae('pinch', { cell: curCell, auto: autoCell });
    }
    view.addEventListener('touchend', function (e) { if (e.touches.length < 2) endPinch(); });
    view.addEventListener('touchcancel', endPinch);
    // Ctrl + wheel (and a trackpad pinch, which browsers report the same way).
    view.addEventListener('wheel', function (e) {
        if (!e.ctrlKey) return;
        e.preventDefault();
        zoom(e.deltaY < 0 ? 1 : -1);
    }, { passive: false });

    // A cell half off screen is not a target: a finger aiming at the edge of
    // the visible board must not open what it cannot see.
    function halfHidden(i) {
        var r = cells[i].getBoundingClientRect(), v = view.getBoundingClientRect();
        var w = Math.min(r.right, v.right, window.innerWidth) - Math.max(r.left, v.left, 0);
        var h = Math.min(r.bottom, v.bottom, window.innerHeight) - Math.max(r.top, v.top, 0);
        return w < r.width * 0.6 || h < r.height * 0.6;
    }

    // ── Chrome: counters, face, status, buttons ──────────────────────────────
    function setFace(f) { $('msNew').setAttribute('data-face', f); }
    function restFace() { setFace(game.status === 'won' ? 'win' : (game.status === 'lost' ? 'lost' : 'idle')); }
    function updateHud() {
        var left = game.minesLeft();
        setText($('msMines'), String(left));
        $('msMinesBox').setAttribute('aria-label', tr('mines_left') + ': ' + left);
    }
    var statusTone = '';
    function setStatus(html, tone) {
        var el = $('msStatus');
        el.textContent = '';
        if (typeof html === 'string') el.textContent = html;
        else if (html) el.appendChild(html);
        tone = tone || '';
        if (tone !== statusTone) { if (tone) el.setAttribute('data-tone', tone); else el.removeAttribute('data-tone'); statusTone = tone; }
    }
    function idleStatus() {
        if (!game) return;
        if (meta.ngFail) { setStatus(tr('st_ng_fallback')); return; }
        // On the whole screen every pixel is the board's: the reminder of the
        // controls stays on the page.
        if (stage.classList.contains('gk-full')) { setStatus(''); return; }
        var touch = lastInput === 'touch';
        if (coarse && game.status !== 'playing' && tallerThanScreen()) { setStatus(tr('tip_fullscreen')); return; }
        if (game.status === 'ready') setStatus(tr(touch ? 'st_ready_touch' : 'st_ready_mouse'));
        else if (game.status === 'playing') setStatus(tr(touch ? (prefs.flagMode ? 'st_play_touch_flag' : 'st_play_touch') : 'st_play_mouse'));
    }
    function tallerThanScreen() {
        return !stage.classList.contains('gk-full') && board.offsetHeight > window.innerHeight * 0.82;
    }
    function syncChrome() {
        var i, btn;
        $('msFull').classList.toggle('ms-btn-main', coarse && tallerThanScreen());
        var lv = $('msLevels').querySelectorAll('[data-level]');
        for (i = 0; i < lv.length; i++) lv[i].setAttribute('aria-checked', lv[i].getAttribute('data-level') === meta.level ? 'true' : 'false');
        var custom = $('msLevels').querySelector('[data-level=custom]');
        custom.title = meta.level === 'custom' ? tr('level_size', { w: game.w, h: game.h, mines: game.count }) : '';
        btn = $('msMode');
        btn.setAttribute('aria-pressed', meta.ng ? 'true' : 'false');
        btn.title = tr(meta.ng ? 'mode_ng_on' : 'mode_ng_off');
        var tools = $('msTool').querySelectorAll('[data-tool]');
        for (i = 0; i < tools.length; i++) tools[i].setAttribute('aria-checked', (tools[i].getAttribute('data-tool') === 'flag') === !!prefs.flagMode ? 'true' : 'false');
        $('msOdds').setAttribute('aria-pressed', oddsOn ? 'true' : 'false');
        var over = game.over();
        stage.setAttribute('data-state', game.status);
        setHidden('msTool', over); setHidden('msHint', over); setHidden('msOdds', over);
        setHidden('msAgain', !over); setHidden('msReplay', !over || !game.mine); setHidden('msShare', !over || !game.mine);
        setHidden('msUndo', game.status !== 'lost');
        updateHud();
        restFace();
        setText($('msTime'), Kit.formatTime(clock.elapsed()));
    }
    function setHidden(id, hidden) { var el = $(id); if (el) el.hidden = !!hidden; }
    $('msStatus').addEventListener('click', function () {
        if (stage.classList.contains('gk-full')) setStatus('');
    });

    // ── Sound and touch feedback (both optional, sound off by default) ───────
    var audio = null;
    function beep(kind) {
        if (!prefs.sound) return;
        try {
            var AC = window.AudioContext || window.webkitAudioContext;
            if (!AC) return;
            if (!audio) audio = new AC();
            if (audio.state === 'suspended') audio.resume();
            var f = { open: 520, flag: 330, chord: 620, win: 880, lose: 110 }[kind] || 440;
            var o = audio.createOscillator(), g = audio.createGain(), t0 = audio.currentTime, len = kind === 'win' || kind === 'lose' ? 0.32 : 0.06;
            o.type = kind === 'lose' ? 'sawtooth' : 'sine';
            o.frequency.setValueAtTime(f, t0);
            if (kind === 'win') o.frequency.exponentialRampToValueAtTime(f * 1.5, t0 + len);
            g.gain.setValueAtTime(0.0001, t0);
            g.gain.exponentialRampToValueAtTime(0.12, t0 + 0.008);
            g.gain.exponentialRampToValueAtTime(0.0001, t0 + len);
            o.connect(g); g.connect(audio.destination);
            o.start(t0); o.stop(t0 + len + 0.02);
        } catch (_) {}
    }
    function buzz(ms) {
        if (!prefs.vibrate) return;
        try { if (navigator.vibrate) navigator.vibrate(ms); } catch (_) {}
    }

    // ── Games ────────────────────────────────────────────────────────────────
    function blankMeta(level, ng) {
        return {
            level: level, ng: !!ng, first: -1, daily: null, beat: 0, fixed: false, covered: false,
            rank: level === 'custom' ? 'none' : 'full', ngFail: false, told: null, input: {}, shared: false
        };
    }
    function newGame(opts) {
        opts = opts || {};
        var level = opts.level || prefs.level, d = opts.dims || dimsOf(level);
        if (pre) pre = null;
        busy = false;
        clearMarks();
        game = new Core.Game({ w: d.w, h: d.h, mines: d.mines, questions: prefs.questions });
        var ng = opts.ng != null ? opts.ng : (prefs.ng && d.mines <= Gen.noGuessLimit(d.w, d.h));
        meta = blankMeta(level, ng);
        if (opts.fixed) {
            game.setMines(opts.fixed.mine);
            meta.first = opts.fixed.start;
            meta.fixed = true;
            meta.rank = opts.rank || 'none';
            meta.daily = opts.daily || null;
            meta.beat = opts.beat || 0;
            meta.shared = !!opts.shared;
        }
        clock.reset(0);
        showResult(null);
        setCover(opts.cover || null);
        gesture.reset();
        layout(true);
        view.scrollLeft = Math.max(0, (view.scrollWidth - view.clientWidth) / 2);
        if (stage.classList.contains('gk-full')) view.scrollTop = Math.max(0, (view.scrollHeight - view.clientHeight) / 2);
        syncChrome();
        idleStatus();
        if (opts.fixed && !opts.cover) beginFixed(false);
        persist();
        poolFor();
        if (oddsOn) refreshOdds();
    }

    // A board whose mines are already known (board of the day, a link, a
    // replay): the opening is the system's move, the same for everyone.
    function beginFixed(startClock) {
        meta.covered = false;
        stage.removeAttribute('data-covered');
        var res = game.open(meta.first);
        paintList(res.changed);
        if (startClock) clock.start();
        syncChrome();
        idleStatus();
        persist();
    }
    function setCover(c) {
        var el = $('msCover');
        if (!c) { el.hidden = true; stage.removeAttribute('data-covered'); if (meta) meta.covered = false; return; }
        setText($('msCoverTitle'), c.title);
        setText($('msCoverNote'), c.note || '');
        el.hidden = false;
        stage.setAttribute('data-covered', '1');
        meta.covered = true;
    }
    $('msCoverStart').addEventListener('click', function () {
        if (!meta || !meta.covered) return;
        $('msCover').hidden = true;
        if (meta.daily) noteDailyTry();
        if (meta.shared) { try { history.replaceState(null, '', location.pathname + location.search); } catch (_) {} }
        beginFixed(true);
        stageMark();
        cells[meta.first].focus({ preventScroll: true });
    });

    function stageMark() {
        try { if (typeof window.__stage === 'function') window.__stage('playing', { level: meta.level, mode: meta.ng ? 'ng' : 'classic' }); } catch (_) {}
        try { if (typeof window.__aeFunnel === 'function') window.__aeFunnel('game_start', { level: meta.level, mode: meta.ng ? 'ng' : 'classic', daily: meta.daily ? 1 : 0, shared: meta.shared ? 1 : 0, input: lastInput }); } catch (_) {}
    }

    // The first click: the board is made for it.
    function startGame(first) {
        if (busy) return;
        busy = true;
        var slow = setTimeout(function () { if (busy) setStatus(tr('st_generating')); }, 250);
        var p = (pre && pre.first === first && pre.game === game) ? pre.p : requestBoard(first);
        var mine = game;
        pre = null;
        p.then(function (res) {
            clearTimeout(slow);
            if (game !== mine || game.placed()) return;
            busy = false;
            var layoutBytes = res.mine instanceof Uint8Array ? res.mine : new Uint8Array(res.mine);
            game.setMines(layoutBytes);
            meta.first = first;
            if (meta.ng && res.ok === false) { meta.ng = false; meta.ngFail = true; meta.rank = 'none'; }
            if (oddsOn) game.assisted = true;
            clock.reset(0);
            stageMark();
            ae('board', { level: meta.level, mode: meta.ng ? 'ng' : 'classic', from: res.from || '', ms: res.ms || 0, attempts: res.attempts || 0 });
            applyMove(game.open(first), null, 'open');
        }, function (err) {
            clearTimeout(slow);
            busy = false;
            idleStatus();
            try { if (typeof window.__handleError === 'function') window.__handleError(err, 'minesweeper_board', {}); } catch (_) {}
        });
    }
    function prefetch(first) {
        if (!game || game.placed() || busy || meta.fixed || meta.covered) return;
        if (game.st[first] !== HIDDEN) return;
        if (pre && pre.first === first && pre.game === game) return;
        pre = { first: first, game: game, p: requestBoard(first) };
    }

    function applyMove(res, before, kind) {
        if (res.noop) return;
        bootMoved = true;
        clearMarks();
        paintList(res.changed);
        if (game.status === 'playing' && !clock.running()) clock.start();
        updateHud();
        if (res.boom) { lose(res.boom, before, kind === 'chord'); return; }
        if (res.won) { win(); return; }
        if (kind === 'flag') {
            beep('flag');
            var on = game.st[res.changed[0]] === FLAG;
            say(tr(on ? 'say_flag' : 'say_unflag', { m: game.minesLeft() }));
        } else {
            beep(kind === 'chord' ? 'chord' : 'open');
            say(tr('say_opened', { n: res.changed.length, m: game.minesLeft() }));
        }
        if (stage.getAttribute('data-state') !== game.status) syncChrome();
        idleStatus();
        if (oddsOn) refreshOdds();
        persist();
    }
    function doOpen(i) {
        if (!game.placed()) { if (game.st[i] === HIDDEN || game.st[i] === QUESTION) startGame(i); return; }
        var before = game.view();
        applyMove(game.open(i), before, 'open');
    }
    function doFlag(i, src) {
        var res = game.toggleFlag(i);
        if (!res.noop && src === 'hold') buzz(14);
        applyMove(res, null, 'flag');
    }
    function doChord(i) {
        if (!game.placed()) return;
        var before = game.view();
        applyMove(game.chord(i), before, 'chord');
    }
    function noteInput(src) {
        var kind = src === 'tap' || src === 'hold' ? 'touch' : (src === 'key' ? 'key' : 'mouse');
        if (meta) meta.input[kind] = (meta.input[kind] || 0) + 1;
        if (kind !== 'key' && kind !== lastInput) { lastInput = kind; }
    }
    function playable() { return game && !game.over() && !busy && !meta.covered; }

    function primary(i, src) {
        if (!playable()) return;
        noteInput(src);
        if (src === 'tap' && halfHidden(i)) {
            cells[i].scrollIntoView({ block: 'nearest', inline: 'nearest' });
            setStatus(tr('st_edge'));
            ae('edge_guard', {});
            return;
        }
        if (game.st[i] === OPEN) {
            if (game.num[i] > 0 && prefs.chord) doChord(i);
            return;
        }
        // The keyboard has a key for each action; the Dig / Flag switch is for pointers.
        if (prefs.flagMode && src !== 'key') doFlag(i, src); else doOpen(i);
    }
    function secondary(i, src) {
        if (!playable()) return;
        noteInput(src);
        if (game.st[i] === OPEN) return;
        if (prefs.flagMode && src !== 'key') { if (src === 'hold') buzz(14); doOpen(i); }
        else doFlag(i, src);
    }
    function chord(i, src) {
        if (!playable()) return;
        noteInput(src);
        if (game.st[i] === OPEN && game.num[i] > 0) doChord(i);
    }

    // ── Gestures ─────────────────────────────────────────────────────────────
    var pressed = [], pressKey = '';
    function showPress(list) {
        var i;
        for (i = 0; i < pressed.length; i++) if (cells[pressed[i]]) cells[pressed[i]].classList.remove('is-down');
        pressed = list;
        for (i = 0; i < pressed.length; i++) cells[pressed[i]].classList.add('is-down');
    }
    var gesture = Gesture.create({
        holdMs: HOLD[prefs.hold],
        canHold: function (i) { return playable() && game.st[i] !== OPEN; },
        primary: primary,
        secondary: secondary,
        chord: chord,
        press: function (i, kind) {
            // Called on every mouse move while a button is down: act only on a change.
            var key = kind + ':' + i;
            if (key === pressKey) return;
            pressKey = key;
            if (!playable() || i < 0 || kind === 'none') { showPress([]); restFace(); return; }
            var list = [];
            if (kind === 'single') { if (game.st[i] === HIDDEN || game.st[i] === QUESTION) list.push(i); }
            else {
                var nb = game.nb[i];
                for (var k = 0; k < nb.length; k++) if (game.st[nb[k]] === HIDDEN || game.st[nb[k]] === QUESTION) list.push(nb[k]);
                if (game.st[i] === HIDDEN) list.push(i);
            }
            showPress(list);
            setFace('press');
        },
        holdStart: function (i, ms) {
            var el = cells[i];
            ring.style.left = (el.offsetLeft + el.offsetWidth / 2) + 'px';
            ring.style.top = (el.offsetTop + el.offsetHeight / 2) + 'px';
            ring.style.setProperty('--ms-hold', ms + 'ms');
            ring.hidden = false;
            ring.classList.remove('is-on');
            void ring.getBoundingClientRect();
            ring.classList.add('is-on');
        },
        holdEnd: function () {
            ring.classList.remove('is-on');
            ring.hidden = true;
        }
    });
    function cellOf(target) {
        var el = target && target.closest ? target.closest('.ms-c') : null;
        return el && board.contains(el) ? +el.getAttribute('data-i') : -1;
    }
    function cellAt(e) {
        if (e.pointerType === 'mouse' || e.pointerType === 'pen') return cellOf(document.elementFromPoint(e.clientX, e.clientY));
        return cellOf(e.target);
    }
    var tracking = false;
    function onMove(e) { gesture.move(e, e.pointerType === 'mouse' ? cellAt(e) : -1); }
    function onUp(e) { gesture.up(e, cellAt(e)); untrackIfIdle(); }
    function onCancel(e) { gesture.cancel(e); untrackIfIdle(); }
    function untrackIfIdle() {
        var s = gesture.state();
        if (s.mouse || (s.finger && !s.finger.ended)) return;
        if (!tracking) return;
        tracking = false;
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onCancel);
    }
    board.addEventListener('pointerdown', function (e) {
        var i = cellOf(e.target);
        if (i >= 0 && e.pointerType !== 'mouse') prefetch(i);
        if (i >= 0 && e.pointerType === 'mouse' && e.button === 0 && !prefs.flagMode) prefetch(i);
        gesture.down(e, i);
        if (!tracking) {
            tracking = true;
            document.addEventListener('pointermove', onMove);
            document.addEventListener('pointerup', onUp);
            document.addEventListener('pointercancel', onCancel);
        }
    });
    board.addEventListener('click', function (e) { gesture.click(e, cellOf(e.target)); });
    board.addEventListener('contextmenu', function (e) { if (gesture.contextmenu(e, cellOf(e.target))) e.preventDefault(); });
    // The middle button would start the browser's auto-scroll; a drag would pick the cell up.
    board.addEventListener('mousedown', function (e) { if (e.button === 1) e.preventDefault(); });
    board.addEventListener('auxclick', function (e) { e.preventDefault(); });
    board.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // Keyboard: arrows move, Space / Enter open, F flags.
    board.addEventListener('focusin', function (e) {
        var i = cellOf(e.target);
        if (i < 0 || i === focusIdx) return;
        if (cells[focusIdx]) cells[focusIdx].setAttribute('tabindex', '-1');
        focusIdx = i;
        cells[i].setAttribute('tabindex', '0');
    });
    board.addEventListener('keydown', function (e) {
        if (e.altKey || e.ctrlKey || e.metaKey) return;
        var i = cellOf(e.target);
        if (i < 0) return;
        var pos = cellPos(i), c = pos.c, r = pos.r, moved = true, k = e.key;
        if (k === 'ArrowLeft') c--; else if (k === 'ArrowRight') c++; else if (k === 'ArrowUp') r--; else if (k === 'ArrowDown') r++;
        else if (k === 'Home') c = 0; else if (k === 'End') c = colsD - 1;
        else moved = false;
        if (moved) {
            e.preventDefault();
            c = clamp(c, 0, colsD - 1); r = clamp(r, 0, rowsD - 1);
            var next = cellIndex(c, r);
            cells[next].focus({ preventScroll: true });
            cells[next].scrollIntoView({ block: 'nearest', inline: 'nearest' });
            return;
        }
        // Letters by their place on the keyboard, not by the layout in use.
        if (k === ' ' || k === 'Enter' || k === 'Spacebar') { e.preventDefault(); primary(i, 'key'); }
        else if (e.code === 'KeyF') { e.preventDefault(); secondary(i, 'key'); }
    });
    stage.addEventListener('keydown', function (e) {
        if (e.altKey || e.ctrlKey || e.metaKey || e.defaultPrevented) return;
        var tag = e.target && e.target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (stage.querySelector('dialog[open]')) return;
        if (e.code === 'KeyN' || e.key === 'F2') { e.preventDefault(); againSame(); }
        else if (e.code === 'KeyH') { e.preventDefault(); doHint(); }
    });

    // ── Hint, odds ───────────────────────────────────────────────────────────
    function currentView() { return game.view(); }
    function helped() {
        if (!game.assisted) { game.assisted = true; }
    }
    function hintText(h) {
        var rule = h.proof ? h.proof.rule : '';
        if (h.kind === 'wrongflag') return tr('hint_wrongflag');
        if (h.kind === 'safe' || h.kind === 'mine') {
            if (rule === 'single') {
                var n = game.num[h.proof.numbers[0]];
                return tr(h.kind === 'safe' ? 'hint_safe_single' : 'hint_mine_single', { n: n, k: h.proof.need });
            }
            if (rule === 'count') return tr(h.kind === 'safe' ? 'hint_safe_count' : 'hint_mine_count', { n: h.proof.minesLeft != null ? h.proof.minesLeft : game.minesLeft() });
            return tr('hint_' + h.kind + '_' + (rule === 'pair' ? 'pair' : 'region'));
        }
        if (h.kind === 'guess') {
            if (Math.abs(h.risk - 0.5) < 1e-9 && h.ties > 1) return tr('hint_guess_even');
            return tr('hint_guess', { p: Math.round(h.risk * 100) });
        }
        if (h.kind === 'none' && h.reason === 'too-large') return tr('hint_toolarge');
        return tr('hint_none');
    }
    function showProof(proof) {
        if (!proof) return;
        var k;
        for (k = 0; k < (proof.numbers || []).length; k++) setMark(proof.numbers[k], 'is-proof');
        for (k = 0; k < (proof.flags || []).length; k++) setMark(proof.flags[k], game.st[proof.flags[k]] === FLAG ? 'is-proof-flag' : 'is-hot');
    }
    function doHint() {
        if (!playable() || !game.placed()) return;
        var v = currentView(), near = focusIdx, trust = meta.told, g0 = game;
        analyse('hint', { view: v, near: near, trust: trust }, function () { return Solver.hint(v, { near: near, trust: trust }); }).then(function (h) {
            if (game !== g0 || game.over() || !h) return;
            clearMarks();
            helped();
            game.hints++;
            var k;
            if (h.kind === 'safe' || h.kind === 'wrongflag') for (k = 0; k < h.cells.length; k++) setMark(h.cells[k], 'is-safe');
            else if (h.kind === 'mine') {
                if (!meta.told) meta.told = new Uint8Array(game.n);
                for (k = 0; k < h.cells.length; k++) { setMark(h.cells[k], 'is-hot'); meta.told[h.cells[k]] = 1; }
            } else if (h.kind === 'guess') {
                showOdds({ prob: h.prob, safe: [], mines: [] });
                setMark(h.cells[0], 'is-best');
            }
            showProof(h.proof);
            paintList(Object.keys(marks).map(Number));
            setStatus(hintText(h), h.kind === 'guess' || h.kind === 'none' ? '' : 'good');
            if (h.cells && h.cells.length) cells[h.cells[0]].scrollIntoView({ block: 'nearest', inline: 'nearest' });
            ae('hint', { kind: h.kind, rule: h.proof ? h.proof.rule : '', level: meta.level, mode: meta.ng ? 'ng' : 'classic' });
            persist();
        });
    }
    function showOdds(a) {
        var i;
        if (!a.prob) return false;
        for (i = 0; i < game.n; i++) {
            if (game.st[i] === OPEN) continue;
            var p = a.prob[i];
            if (p < 0 || p == null) continue;
            if (game.st[i] === FLAG && p > 1 - 1e-9) continue;
            if (p < 1e-9) setMark(i, 'is-safe');
            else if (p > 1 - 1e-9) setMark(i, 'is-hot');
            else setMark(i, '', p);
        }
        oddsShown = true;
        return true;
    }
    function refreshOdds() {
        if (!game || game.over() || !game.placed()) return;
        var v = currentView(), g0 = game;
        analyse('odds', { view: v }, function () {
            var a = Solver.analyze(v, { probabilities: true });
            return { status: a.status, prob: a.prob, safe: a.safe.concat(a.safeByCount), mines: a.mines.concat(a.minesByCount) };
        }).then(function (a) {
            if (game !== g0 || !oddsOn || game.over() || !a) return;
            clearMarks();
            if (!showOdds(a)) { setStatus(tr('odds_partial')); return; }
            paintList(Object.keys(marks).map(Number));
            setStatus(tr('odds_on'));
        });
    }
    $('msHint').addEventListener('click', doHint);
    $('msOdds').addEventListener('click', function () {
        oddsOn = !oddsOn;
        syncChrome();
        if (oddsOn) {
            if (game.placed() && !game.over()) { helped(); persist(); }
            refreshOdds();
            ae('odds', { on: 1 });
        } else { clearMarks(); idleStatus(); }
    });

    // ── The end of a game ────────────────────────────────────────────────────
    function inputKind() {
        var best = 'mouse', top = -1;
        for (var k in meta.input) if (meta.input[k] > top) { top = meta.input[k]; best = k; }
        return best;
    }
    function complete(won, ms) {
        var c = game.clicks;
        try {
            if (typeof window.__aeToolComplete === 'function') {
                window.__aeToolComplete({
                    level: meta.level, mode: meta.ng ? 'ng' : 'classic', won: won ? 1 : 0, ms: ms, bv: game.bv, bv_done: game.solvedBV(),
                    hints: game.hints, undos: game.undos, assisted: game.assisted ? 1 : 0, input: inputKind(),
                    clicks: game.totalClicks(), chords: c.chord, flags: c.flag, daily: meta.daily ? 1 : 0, shared: meta.shared ? 1 : 0,
                    w: game.w, h: game.h, mines: game.count, rank: meta.rank
                });
            }
        } catch (_) {}
        var counts = gesture.takeCounts();
        if (counts) ae('gesture', counts);
    }
    function fact(label, value) {
        var d = document.createElement('div'), dt = document.createElement('dt'), dd = document.createElement('dd');
        d.className = 'ms-fact';
        dt.textContent = label; dd.textContent = value;
        d.appendChild(dt); d.appendChild(dd);
        return d;
    }
    function showResult(r) {
        var el = $('msResult');
        if (!r) { el.hidden = true; return; }
        setText($('msResultHead'), r.head);
        setText($('msResultSub'), r.sub || '');
        var facts = $('msFacts');
        facts.textContent = '';
        for (var i = 0; i < r.facts.length; i++) facts.appendChild(fact(r.facts[i][0], r.facts[i][1]));
        el.hidden = false;
    }
    function pct(x) { return Math.round(x * 100) + '%'; }
    // Several short messages as one line: each ends as a sentence, whatever its translation ended with.
    function sentences(list) {
        return list.filter(Boolean).map(function (s) { s = String(s).trim(); return /[.!?。؟]$/.test(s) ? s : s + '.'; }).join(' ');
    }
    function time(ms) { return Kit.formatTime(ms, { tenths: true }); }

    function win() {
        clock.pause();
        var ms = clock.elapsed(), rec = null, sub = [], sum;
        paintAll();
        beep('win');
        var assisted = game.assisted;
        if (meta.rank === 'full') {
            rec = stats.record({ variant: variantId(), won: true, ms: ms, assisted: assisted, daily: meta.daily || undefined });
            sum = rec.summary;
        } else if (meta.daily) {
            // A day finished with help or on a second try still counts as a day
            // in the streak, apart from the level's own numbers.
            stats.record({ variant: 'daily-practice', won: true, ms: ms, assisted: true, daily: meta.daily });
        }
        if (rec && rec.bestTime) sub.push(tr('win_record'));
        if (assisted || meta.rank !== 'full') { if (assisted) sub.push(tr('win_assisted')); }
        if (meta.beat) sub.push(tr(ms < meta.beat ? 'win_beat' : 'win_not_beat', { time: time(meta.beat) }));
        if (meta.daily) sub.push(tr('daily_streak', { n: stats.dailyStreak(Kit.dailyKey()) }));
        var secs = Math.max(ms, 1) / 1000, clicks = game.totalClicks(), c = game.clicks;
        var facts = [
            [tr('r_time'), time(ms)],
            [tr('r_3bv'), String(game.bv)],
            [tr('r_3bvs'), (game.bv / secs).toFixed(2)],
            [tr('r_clicks'), (c.open + c.chord + c.wasted) + '+' + c.flag],
            [tr('r_eff'), pct(game.bv / Math.max(1, clicks))]
        ];
        if (sum) {
            if (sum.bestMs != null) facts.push([tr('r_best'), time(sum.bestMs)]);
            facts.push([tr('r_wins'), sum.won + '/' + sum.played]);
            facts.push([tr('r_streak'), String(sum.streak)]);
        }
        if (game.hints) facts.push([tr('r_hints'), String(game.hints)]);
        showResult({ head: tr('win_title') + ' — ' + time(ms), sub: sentences(sub), facts: facts });
        setStatus(sentences([tr('win_title') + ' — ' + time(ms), rec && rec.bestTime ? tr('win_record') : '']), 'good');
        say(tr('say_win', { time: time(ms) }));
        syncChrome();
        forget();
        complete(true, ms);
    }

    function lose(boom, before, wasChord) {
        clock.pause();
        var ms = clock.elapsed(), g0 = game;
        paintAll();
        beep('lose');
        buzz([30, 40, 60]);
        if (meta.rank === 'full') stats.record({ variant: variantId(), won: false, ms: ms });
        var done = game.bv ? game.solvedBV() / game.bv : 0;
        showResult({ head: tr('loss_title'), sub: '', facts: [[tr('r_cleared'), pct(done)], [tr('r_time'), time(ms)], [tr('r_clicks'), String(game.totalClicks())]] });
        setStatus(tr('loss_title'), 'bad');
        say(tr('say_loss'));
        syncChrome();
        forget();
        complete(false, ms);
        if (!before) return;
        analyse('loss', { view: before, cell: boom[0] }, function () { return Solver.explainLoss(before, boom[0]); }).then(function (v) {
            if (game !== g0 || game.status !== 'lost' || !v) return;
            var text, k;
            clearMarks();
            if (v.verdict === 'certain-mine') { text = tr('loss_certain'); showProof(v.proof); }
            else if (v.verdict === 'safe-existed') {
                text = tr('loss_safe_existed');
                // Текст говорит об ОДНОЙ клетке; доказательство открывает иногда несколько —
                // выделяется ближайшая к взорвавшейся, чтобы фраза и картинка совпадали.
                var ex = v.example || [], pick = -1, pd = 1e9, bx = boom[0] % game.w, by = (boom[0] / game.w) | 0;
                for (k = 0; k < ex.length; k++) {
                    var dd = Math.max(Math.abs(ex[k] % game.w - bx), Math.abs(((ex[k] / game.w) | 0) - by));
                    if (dd < pd) { pd = dd; pick = ex[k]; }
                }
                if (pick >= 0) setMark(pick, 'is-safe');
                showProof(v.proof);
            } else if (v.verdict === 'forced-guess') {
                if (v.even && v.pickedBest) text = tr('loss_forced_even');
                else if (v.pickedBest) text = tr('loss_forced_best', { p: Math.round(v.risk * 100) });
                else {
                    text = tr('loss_forced', { p: Math.round(v.risk * 100), b: Math.round(v.bestRisk * 100) });
                    for (k = 0; k < v.best.length && k < 6; k++) setMark(v.best[k], 'is-best');
                }
            } else text = tr('loss_unknown');
            if (wasChord) text = tr('loss_wrong_flag') + ' ' + text;
            paintList(Object.keys(marks).map(Number));
            setText($('msResultSub'), text);
            setStatus(text, 'bad');
            ae('loss_verdict', { verdict: v.verdict, chord: wasChord ? 1 : 0, level: meta.level, mode: meta.ng ? 'ng' : 'classic', risk: v.risk != null ? Math.round(v.risk * 100) : -1 });
        });
    }

    function againSame() { newGame({}); }
    $('msNew').addEventListener('click', againSame);
    $('msAgain').addEventListener('click', againSame);
    $('msUndo').addEventListener('click', function () {
        if (game.status !== 'lost') return;
        clearMarks();
        game.undoLoss();
        // What follows is practice: the loss stays the result of this game.
        if (meta.rank === 'full') meta.rank = meta.daily ? 'daily-retry' : 'none';
        showResult(null);
        paintAll();
        syncChrome();
        clock.start();
        setStatus(tr('st_assisted'));
        persist();
        ae('undo', { level: meta.level });
    });
    $('msReplay').addEventListener('click', function () {
        if (!game.mine || meta.first < 0) return;
        var keep = { mine: game.mine, start: meta.first };
        newGame({ level: meta.level, dims: { w: game.w, h: game.h, mines: game.count }, ng: meta.ng, fixed: keep, rank: meta.daily ? 'daily-retry' : 'none', daily: meta.daily, beat: meta.beat });
        setStatus(tr('st_assisted'));
        ae('replay', { level: meta.level });
    });

    // ── Links ────────────────────────────────────────────────────────────────
    function boardLink(ms) {
        var code = Core.encodeBoard({ w: game.w, h: game.h, mine: game.mine, start: meta.first, noGuess: !!meta.ng });
        return location.origin + location.pathname + '#b=' + code + (ms ? '&t=' + ms : '');
    }
    $('msShare').addEventListener('click', function () {
        if (!game.mine || meta.first < 0) return;
        var ms = game.status === 'won' ? clock.elapsed() : 0, url = boardLink(ms);
        var text = ms ? tr('share_text', { level: levelName(meta.level), time: time(ms) }) : '';
        ae('share', { won: ms ? 1 : 0, level: meta.level });
        if (coarse && navigator.share) {
            navigator.share({ title: document.title, text: text, url: url }).catch(function () {});
            return;
        }
        var done = function () { toast(tr('share_copied'), 'success'); };
        if (navigator.clipboard && navigator.clipboard.writeText) navigator.clipboard.writeText(url).then(done, function () { legacyCopy(url); done(); });
        else { legacyCopy(url); done(); }
    });
    function legacyCopy(text) {
        try {
            var ta = document.createElement('textarea');
            ta.value = text; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.opacity = '0';
            stage.appendChild(ta); ta.select(); document.execCommand('copy'); stage.removeChild(ta);
        } catch (_) {}
    }
    function readLink() {
        var h = String(location.hash || '').replace(/^#/, '');
        if (!/(^|&)b=/.test(h)) return null;
        var out = {}, parts = h.split('&');
        for (var i = 0; i < parts.length; i++) { var kv = parts[i].split('='); out[kv[0]] = kv.slice(1).join('='); }
        var b = Core.decodeBoard(out.b);
        if (!b) return { broken: true };
        var t = parseInt(out.t, 10);
        return { board: b, beat: t > 0 && t < 36000000 ? t : 0 };
    }
    function openShared(link) {
        var b = link.board, level = Core.levelOf(b.w, b.h, b.count);
        newGame({
            level: level, dims: { w: b.w, h: b.h, mines: b.count }, ng: b.noGuess, shared: true, beat: link.beat, rank: 'none',
            fixed: { mine: b.mine, start: b.start },
            cover: { title: tr('share_title'), note: link.beat ? tr('share_note', { time: time(link.beat) }) : tr('share_note_plain') }
        });
        if (level === 'custom') { prefs.cw = b.w; prefs.ch = b.h; prefs.cm = b.count; }
    }

    // ── Board of the day ─────────────────────────────────────────────────────
    function dailyTries() {
        var d = load('daily_try', null), key = Kit.dailyKey();
        return (d && d.key === key && d.levels) ? d : { key: key, levels: {} };
    }
    function noteDailyTry() {
        var d = dailyTries();
        d.levels[meta.level] = (d.levels[meta.level] || 0) + 1;
        save('daily_try', d);
    }
    function dateLabel(key) {
        try {
            var p = key.split('-'), loc = typeof window.__intlLocale === 'function' ? window.__intlLocale() : undefined;
            return new Date(+p[0], +p[1] - 1, +p[2]).toLocaleDateString(loc, { day: 'numeric', month: 'long' });
        } catch (_) { return key; }
    }
    function openDaily() {
        var level = Core.LEVELS[prefs.level] ? prefs.level : 'intermediate', L = Core.LEVELS[level];
        var key = Kit.dailyKey(), seed = Kit.dailySeed(key, 'minesweeper:' + level);
        var first = Kit.rng(seed).fork('start').int(L.w * L.h);
        var tried = dailyTries().levels[level] || 0, done = stats.dailyResult(key, level + ':ng');
        setStatus(tr('st_generating'));
        var probe = { w: L.w, h: L.h, mines: L.mines, first: first, mode: 'ng', seed: seed, type: 'gen' };
        ask(probe, 8000).then(null, function () { return localBoard(probe); }).then(function (res) {
            var mine = res.mine instanceof Uint8Array ? res.mine : new Uint8Array(res.mine);
            var note = [tr('daily_note')];
            if (done) note = [tr('daily_done', { time: time(done.ms || 0) }), tr('daily_retry')];
            else if (tried) note.push(tr('daily_retry'));
            prefs.level = level;
            newGame({
                level: level, dims: L, ng: true, daily: key, rank: tried || done ? 'daily-retry' : 'full',
                fixed: { mine: mine, start: first },
                cover: { title: tr('daily_title', { date: dateLabel(key) }) + ' — ' + levelName(level), note: sentences(note) }
            });
            ae('daily', { level: level, tried: tried, done: done ? 1 : 0 });
        });
    }
    $('msDaily').addEventListener('click', openDaily);

    // ── Level, mode, tool ────────────────────────────────────────────────────
    $('msLevels').addEventListener('click', function (e) {
        var b = e.target.closest('[data-level]');
        if (!b) return;
        var level = b.getAttribute('data-level');
        if (level === 'custom') { openCustom(); return; }
        prefs.level = level;
        prefs.cell = 0;
        savePrefs();
        newGame({});
    });
    $('msMode').addEventListener('click', function () {
        prefs.ng = !prefs.ng;
        savePrefs();
        newGame({});
        setStatus(tr(meta.ng ? 'mode_ng_on' : 'mode_ng_off'));
        ae('mode', { ng: prefs.ng ? 1 : 0 });
    });
    $('msTool').addEventListener('click', function (e) {
        var b = e.target.closest('[data-tool]');
        if (!b) return;
        prefs.flagMode = b.getAttribute('data-tool') === 'flag';
        savePrefs();
        syncChrome();
        idleStatus();
        ae('flag_mode', { on: prefs.flagMode ? 1 : 0 });
    });
    $('msZoomIn').addEventListener('click', function () { zoom(1); });
    $('msZoomOut').addEventListener('click', function () { zoom(-1); });
    $('msSettingsDlg').addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-zoom]');
        if (!b) return;
        var dir = +b.getAttribute('data-zoom');
        if (dir) zoom(dir);
        else { prefs.cell = 0; savePrefs(); layout(false); }
    });

    // ── Dialogs ──────────────────────────────────────────────────────────────
    function openDialog(dlg) {
        if (typeof dlg.showModal === 'function') { try { dlg.showModal(); return; } catch (_) {} }
        dlg.setAttribute('open', '');
    }
    function closeDialog(dlg) {
        if (typeof dlg.close === 'function') { try { dlg.close(); return; } catch (_) {} }
        dlg.removeAttribute('open');
    }
    Array.prototype.forEach.call(stage.querySelectorAll('dialog'), function (dlg) {
        dlg.addEventListener('click', function (e) {
            if (e.target === dlg || (e.target.closest && e.target.closest('[data-close]'))) closeDialog(dlg);
        });
    });

    function applyLook() {
        app.setAttribute('data-skin', prefs.skin === 'classic' ? 'classic' : 'modern');
        if (prefs.palette) app.setAttribute('data-palette', 'cvd'); else app.removeAttribute('data-palette');
        app.setAttribute('data-hand', prefs.left ? 'left' : 'right');
    }
    function syncSettings() {
        var dlg = $('msSettingsDlg'), i;
        var boxes = dlg.querySelectorAll('[data-pref]');
        for (i = 0; i < boxes.length; i++) boxes[i].checked = !!prefs[boxes[i].getAttribute('data-pref')];
        var groups = dlg.querySelectorAll('[data-pref-group]');
        for (i = 0; i < groups.length; i++) {
            var key = groups[i].getAttribute('data-pref-group'), opts = groups[i].querySelectorAll('[data-value]');
            for (var k = 0; k < opts.length; k++) opts[k].setAttribute('aria-checked', opts[k].getAttribute('data-value') === prefs[key] ? 'true' : 'false');
        }
    }
    $('msSettings').addEventListener('click', function () { syncSettings(); openDialog($('msSettingsDlg')); });
    $('msSettingsDlg').addEventListener('change', function (e) {
        var key = e.target.getAttribute && e.target.getAttribute('data-pref');
        if (!key) return;
        prefs[key] = !!e.target.checked;
        savePrefs();
        settingChanged(key);
    });
    $('msSettingsDlg').addEventListener('click', function (e) {
        var b = e.target.closest && e.target.closest('[data-value]');
        if (!b) return;
        var key = b.parentNode.getAttribute('data-pref-group');
        prefs[key] = b.getAttribute('data-value');
        savePrefs();
        syncSettings();
        settingChanged(key);
    });
    function settingChanged(key) {
        ae('setting', { key: key, value: String(prefs[key]) });
        if (key === 'hold') gesture.setHoldMs(HOLD[prefs.hold]);
        else if (key === 'skin' || key === 'palette' || key === 'left') { applyLook(); layout(false); }
        else if (key === 'rotate') layout(false);
        else if (key === 'questions') game.questions = !!prefs.questions;
        else if (key === 'ng') { newGame({}); }
        if (key === 'sound' && prefs.sound) beep('open');
    }

    function row(cells2, head) {
        var trEl = document.createElement('tr');
        for (var i = 0; i < cells2.length; i++) {
            var c = document.createElement(head || i === 0 ? 'th' : 'td');
            if (i === 0) c.setAttribute('scope', head ? 'col' : 'row'); else if (head) c.setAttribute('scope', 'col');
            c.textContent = cells2[i];
            trEl.appendChild(c);
        }
        return trEl;
    }
    function renderStats() {
        var body = $('msStatsBody'), any = false;
        body.textContent = '';
        var table = document.createElement('table'), thead = document.createElement('thead'), tb = document.createElement('tbody');
        table.className = 'ms-table';
        thead.appendChild(row(['', tr('stats_played'), tr('stats_won'), tr('stats_best'), tr('stats_avg'), tr('stats_streak')], true));
        ['beginner', 'intermediate', 'expert'].forEach(function (level) {
            ['ng', 'cl'].forEach(function (mode) {
                var s = stats.summary(level + ':' + mode);
                if (!s.played) return;
                any = true;
                tb.appendChild(row([
                    levelName(level) + (mode === 'ng' ? ' · ' + tr('mode_ng') : ''), String(s.played),
                    s.won + ' (' + pct(s.winRate || 0) + ')', s.bestMs != null ? time(s.bestMs) : '—', s.avgMs != null ? time(s.avgMs) : '—', String(s.bestStreak)
                ]));
            });
        });
        if (!any) {
            var p = document.createElement('p');
            p.className = 'ms-note';
            p.textContent = tr('stats_empty');
            body.appendChild(p);
        } else {
            table.appendChild(thead); table.appendChild(tb);
            body.appendChild(table);
        }
        var streak = stats.dailyStreak(Kit.dailyKey());
        if (streak) {
            var d = document.createElement('p');
            d.className = 'ms-note';
            d.textContent = tr('daily') + '. ' + tr('daily_streak', { n: streak });
            body.appendChild(d);
        }
        $('msStatsReset').hidden = !any && !streak;
    }
    $('msStats').addEventListener('click', function () { renderStats(); openDialog($('msStatsDlg')); ae('stats_open', {}); });
    $('msStatsReset').addEventListener('click', function () {
        if (!window.confirm(tr('stats_reset_confirm'))) return;
        stats.reset();
        renderStats();
    });

    function customNote() {
        var b = Core.clampBoard($('msCw').value, $('msCh').value, $('msCm').value), limit = Gen.noGuessLimit(b.w, b.h);
        $('msCm').max = Core.maxMines(b.w, b.h);
        setText($('msCustomNote'), prefs.ng ? tr('custom_ng_limit', { n: limit }) : '');
        return b;
    }
    function openCustom() {
        $('msCw').value = prefs.cw; $('msCh').value = prefs.ch; $('msCm').value = prefs.cm;
        customNote();
        openDialog($('msCustomDlg'));
    }
    $('msCustomDlg').addEventListener('input', customNote);
    $('msCustomForm').addEventListener('submit', function (e) {
        e.preventDefault();
        var b = customNote();
        prefs.cw = b.w; prefs.ch = b.h; prefs.cm = b.mines;
        prefs.level = Core.levelOf(b.w, b.h, b.mines);
        prefs.cell = 0;
        savePrefs();
        closeDialog($('msCustomDlg'));
        newGame({});
        ae('custom', { w: b.w, h: b.h, mines: b.mines });
    });

    // ── The whole screen ─────────────────────────────────────────────────────
    var immersive = Kit.immersive(stage, {
        key: SLUG,
        onChange: function (on) {
            $('msFullIcon').setAttribute('href', on ? '#msIExit' : '#msIFull');
            setText($('msFullText'), tr(on ? 'exit_fullscreen' : 'fullscreen'));
            $('msFull').title = tr(on ? 'exit_fullscreen' : 'fullscreen');
            // The screen changed under the board: size it again once the browser has laid it out.
            requestAnimationFrame(function () { layout(false); idleStatus(); syncChrome(); requestAnimationFrame(function () { layout(false); }); });
            ae('fullscreen', { on: on ? 1 : 0 });
        }
    });
    // A second press while the browser is still entering or leaving fullscreen
    // would be answered by the state it was leaving: one press — one change.
    var fullAt = 0;
    $('msFull').addEventListener('click', function (e) {
        if (e.timeStamp - fullAt < 450) return;
        fullAt = e.timeStamp;
        immersive.toggle();
    });

    var resizeRaf = 0;
    function onResize() {
        if (resizeRaf) return;
        resizeRaf = requestAnimationFrame(function () { resizeRaf = 0; layout(false); });
    }
    window.addEventListener('resize', onResize);
    // The stage, not the tray: a line of text appearing under the board must not
    // resize the cells, only a real change of the room does.
    if (window.ResizeObserver) { try { new ResizeObserver(onResize).observe(stage); } catch (_) {} }
    if (window.matchMedia) {
        try {
            window.matchMedia('(pointer:coarse)').addEventListener('change', function (e) { coarse = e.matches; layout(false); });
        } catch (_) {}
    }

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') { gesture.reset(); persist(); }
    });
    // The clock is part of the game: the page writes it down when it leaves
    // (both events — not every browser sends both on a reload) and every two
    // seconds while playing, so a crash costs two seconds of it at most.
    window.addEventListener('pagehide', function () { persist(); });
    window.addEventListener('beforeunload', function () { persist(); });
    saveTimer = setInterval(function () { if (game && game.status === 'playing' && document.visibilityState !== 'hidden') persist(); }, 2000);

    // ── Boot ─────────────────────────────────────────────────────────────────
    applyLook();
    var link = readLink(), early = true;
    if (link && link.board) { durableRead = true; openShared(link); early = false; }
    else {
        if (link && link.broken) toast(tr('share_broken'), 'warning');
        var saved = load(KEY_GAME, null);
        if (saved && restore(saved)) { early = false; setStatus(tr('st_resumed')); }
        else {
            if (saved) drop(KEY_GAME);
            early = prefs.level === 'beginner';
            newGame({});
        }
        // The durable copy may be newer than what localStorage kept through a crash.
        durable.get().then(function (d) {
            durableRead = true;
            if (!d || bootMoved || (link && link.board)) return;
            var have = load(KEY_GAME, null);
            if (have && (have.at || 0) >= (d.at || 0)) return;
            if (game && game.status !== 'ready') return;
            if (restore(d)) { setStatus(tr('st_resumed')); ae('resume', { via: 'durable' }); }
        });
    }
    stage.removeAttribute('data-boot');
    ensureWorker();
    poolFor();
    try {
        if (window.__earlyAction) {
            // A tap made before this script woke up: it is the first move — unless
            // the board on screen was not the one the tap landed on.
            if (early && game.status === 'ready' && !game.placed()) window.__earlyAction.claim(SLUG, function (a) { var i = parseInt(a, 10); if (i >= 0 && i < game.n) primary(i, 'early'); });
            else window.__earlyAction.claim(SLUG);
        }
    } catch (_) {}
    void saveTimer;

    // For the stands and for debugging in the console; the game does not use it.
    window.__minesweeper = {
        game: function () { return game; },
        meta: function () { return meta; },
        prefs: function () { return prefs; },
        gesture: gesture,
        layout: function () { return { cell: curCell, auto: autoCell, turned: turned, cols: colsD, rows: rowsD }; },
        newGame: newGame,
        hint: doHint,
        persist: persist,
        link: boardLink,
        clock: clock
    };
})();
